import React, { useContext } from 'react';
import { AxiosError } from 'axios';
import { Card, CardBody, Typography } from '@one-thd/sui-atomic-components';
import {
  ExperienceContext,
  useConfigService,
} from '@thd-nucleus/experience-context';
import { pushErrorEvent } from '../../../utils/analytics';
import {
  IPaymentCardCommonData,
  TGiftCardBalanceSuccessResponse,
  TPaymentErrorResponse,
} from '../../../models/common/payments';
import { getLastModifiedDate } from '../../../utils/date';
import {
  IMenuItem,
  MeatballsMenu,
} from '../../shared/meatballs-menu/meatballs-menu';
import { getPaymentsErrorCode } from '../../../utils/payments';
import { useModal } from '../../../hooks/use-modal';
import { usePayments } from '../../../hooks/use-payments';
import { CardType } from '../../../constants/payments';
import {
  ACTIVE_GIFT_CARD_SAVES_MSG,
  CHANGES_SAVED_MSG,
  DELETE_GIFT_CARD_MSG,
  INACTIVE_GIFT_CARD_SAVES_MSG,
} from '../../../constants/response-success-messages';
import { ERR_UNABLE_TO_SAVE } from '../../../constants/response-error-messages';
import { IB2BPaymentCard } from '../../../models/b2b/payments';
import { GiftCardLogoAndNumber } from './logo-number';
import {
  DisplayGiftCardBalance,
  IDisplayGiftCardBalanceModalProps,
} from '../display-gift-card-balance-modal/display-gift-card-balance-modal';
import { useMeatballMenuActions } from '../../../hooks/use-meatball-payment-actions';

interface IGiftCardProps {
  card: IPaymentCardCommonData;
  onActionSuccess: (paymentId: string, message: string) => void;
  onActionFailure: (paymentId: string, message: string) => void;
  onCheckBalaceAndReloadDrawerOpen: () => void;
}

export const GiftCard: React.FC<IGiftCardProps> = ({
  card,
  onActionSuccess,
  onActionFailure,
  onCheckBalaceAndReloadDrawerOpen,
}): JSX.Element => {
  const { openModal, hideModal } = useModal();
  const { giftCards } = usePayments();
  const { channel } = useContext(ExperienceContext);
  const {
    onSetActive,
    onSetInactive,
    onEditNickName,
    onViewPurchases,
    onReloadGiftCard,
    onDeleteGiftCard,
    onCheckGiftCardBalance,
    onCheckGiftCardBalanceAndReload,
  } = useMeatballMenuActions();

  const { paymentId } = card;

  const isCardInactive = card.cardStatus?.toLowerCase() === 'inactive';
  const isMobile = channel === 'mobile';

  const { isB2B } = usePayments();

  const shouldUseCheckAndReloadGiftCardBalanceModal =
    useConfigService('ENABLE_NEW_CHECK_AND_RELOAD_GIFT_CARD_BALANCE_MODAL') &&
    isB2B;

  const handleCheckGiftCardBalance = () => {
    const onSuccess = (data: TGiftCardBalanceSuccessResponse) => {
      const props: IDisplayGiftCardBalanceModalProps = {
        card,
        balance: data.availableAmount,
        hideModal,
      };
      hideModal();

      openModal(DisplayGiftCardBalance, props, {
        size: isMobile ? 'sm' : 'md',
      });
    };

    const onFailure = (errorMessage: string) => {
      const props: IDisplayGiftCardBalanceModalProps = {
        card,
        balance: null,
        errorMessage,
        hideModal,
      };
      hideModal();

      openModal(DisplayGiftCardBalance, props, {
        size: isMobile ? 'sm' : 'md',
      });
    };

    onCheckGiftCardBalance(card, false, onSuccess, onFailure);
  };

  const handleEditNickName = () => {
    const currentCard = (giftCards?.response as Array<IB2BPaymentCard>).find(
      (payment) => payment.paymentId === card.paymentId,
    );

    const onSuccess = () => {
      onActionSuccess(card.paymentId, CHANGES_SAVED_MSG.message);
    };

    const onFailure = (error: AxiosError<TPaymentErrorResponse>) => {
      const errorCode = getPaymentsErrorCode(error);
      onActionFailure(card.paymentId, ERR_UNABLE_TO_SAVE.message);
      pushErrorEvent(
        'edit gift card',
        'inline',
        errorCode || 'n/a',
        ERR_UNABLE_TO_SAVE.message,
      );
    };

    onEditNickName(card, currentCard, CardType.GIFT_CARD, onSuccess, onFailure);
  };

  const handleSetActive = () => {
    const onSuccess = () => {
      onActionSuccess(paymentId, ACTIVE_GIFT_CARD_SAVES_MSG.message);
    };
    const onFailure = () => {
      onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
    };

    onSetActive(card, CardType.GIFT_CARD, onSuccess, onFailure);
  };

  const handleSetInactive = () => {
    const onSuccess = () => {
      onActionSuccess(paymentId, INACTIVE_GIFT_CARD_SAVES_MSG.message);
    };
    const onFailure = () => {
      onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
    };
    onSetInactive(card, CardType.GIFT_CARD, onSuccess, onFailure);
  };

  const handleDeleteCard = () => {
    const onSuccess = () => {
      onActionSuccess(paymentId, DELETE_GIFT_CARD_MSG.message);
    };
    const onFailure = (error: AxiosError<TPaymentErrorResponse>) => {
      const errorCode = getPaymentsErrorCode(error);
      onActionFailure(card.paymentId, ERR_UNABLE_TO_SAVE.message);
      pushErrorEvent(
        'gift cards: delete',
        'inline',
        errorCode || 'n/a',
        ERR_UNABLE_TO_SAVE.message,
      );
    };
    onDeleteGiftCard(card, onSuccess, onFailure);
  };

  const menuItems: IMenuItem[] = [
    {
      value: 'Add/Edit Nickname',
      action: handleEditNickName,
      isHidden: !card.actions?.edit,
    },
    {
      value: 'View Purchases',
      action: () => {
        onViewPurchases(card, CardType.GIFT_CARD);
      },
      isHidden: !card.actions?.viewPurchases,
    },
    {
      value: 'Set Active',
      action: handleSetActive,
      isHidden: !card.actions?.setActive,
    },
    {
      value: 'Set Inactive',
      action: handleSetInactive,
      isHidden: !card.actions?.setInactive,
    },
    {
      value: 'Check Gift Card Balance',
      action: () => {
        handleCheckGiftCardBalance();
      },
      isHidden: !!shouldUseCheckAndReloadGiftCardBalanceModal,
    },
    {
      value: 'Reload Gift Card',
      action: onReloadGiftCard,
      isHidden:
        !card.actions?.setInactive ||
        !!shouldUseCheckAndReloadGiftCardBalanceModal,
    },
    {
      value: 'Check Balance & Reload',
      action: () => {
        onCheckGiftCardBalanceAndReload(onCheckBalaceAndReloadDrawerOpen);
      },
      isHidden: !shouldUseCheckAndReloadGiftCardBalanceModal,
    },
    {
      value: 'Delete',
      action: handleDeleteCard,
      isHidden: !card.actions?.delete,
    },
  ];

  return (
    <Card grow>
      <CardBody grow>
        <div
          data-testid="gift-card"
          className="sui-flex sui-flex-col sui-gap-3"
        >
          <div className="sui-flex sui-flex-col sui-gap-1">
            <div
              className={`sui-flex sui-justify-between ${
                card.cardNickName ? 'sui-mb-2' : 'sui-mb-3'
              }`}
            >
              <h3
                className={`sui-font-display sui-text-3xl sui-mr-4 sui-truncate ${
                  isCardInactive ? 'sui-text-inactive' : 'sui-text-primary'
                }`}
              >
                {card.cardNickName}
              </h3>
              <div>
                <MeatballsMenu label="Gift Card" items={menuItems} />
              </div>
            </div>
            <div className="sui-flex sui-flex-wrap">
              <GiftCardLogoAndNumber card={card} shouldShowInactive />
            </div>
          </div>
          {isCardInactive && (
            <div className="sui-mb-6">
              <Typography weight="bold">Inactive</Typography>
            </div>
          )}
          <div>
            {card.addedBy && (
              <div className="sui-overflow-hidden">
                <Typography
                  truncate
                  color={isCardInactive ? 'inactive' : 'subtle'}
                >
                  Added by <span data-qm-mask="">{card.addedBy}</span>
                </Typography>
              </div>
            )}
            {card.lastModifiedDate && (
              <Typography color={isCardInactive ? 'inactive' : 'subtle'}>
                Last edited {getLastModifiedDate(card.lastModifiedDate)}
              </Typography>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
